import { useState, useEffect } from 'react';
import { getDatabase, ref, onValue, off, query, orderByChild, equalTo, update } from 'firebase/database';
import app from '../../services/firebase';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { Typography, Box, Button, Card, CardContent, Stack } from '@mui/material';
import '../../styles/mobidawg-style.scss';

function ResourceModeration() {
    const auth = getAuth(app);
    const navigate = useNavigate();
    const database = getDatabase(app);
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const user = auth.currentUser;
                if (!user) {
                    navigate('/auth/signon');
                    return;
                }

                const idTokenResult = await user.getIdTokenResult(true);
                const isAdmin = idTokenResult.claims.admin;

                if (!isAdmin) {
                    setError("Access Denied: You do not have administrator privileges. Redirecting...");
                    setLoading(false);
                    setTimeout(() => navigate('/'), 5000);
                    return;
                }

                const resourcesRef = ref(database, 'resources');
                const moderationQuery = query(resourcesRef, orderByChild('visibility'), equalTo('moderate'));

                const unsubscribe = onValue(moderationQuery, (snapshot) => {
                    const data = snapshot.val();
                    if (data) {
                        const resourcesToModerate = Object.keys(data).map(key => ({
                            id: key,
                            ...data[key]
                        }));
                        setResources(resourcesToModerate);
                    } else {
                        setResources([]);
                    }
                    setLoading(false);
                }, (err) => {
                    setError(err.message);
                    setLoading(false);
                    console.error("Error fetching resources for moderation:", err);
                });

                return () => off(moderationQuery, 'value', unsubscribe);

            } catch (err) {
                console.error("Error setting up resource moderation:", err);
                setError("Failed to retrieve resources for moderation.");
                setLoading(false);
            }
        };

        fetchData();
    }, [database, auth, navigate]);

    const handleApprove = async (resourceId) => {
        const resourceRef = ref(database, `resources/${resourceId}`);
        await update(resourceRef, { visibility: 'public' });
    };

    const handleReject = async (resourceId) => {
        const resourceRef = ref(database, `resources/${resourceId}`);
        // You could either delete it or move it to a 'rejected' state
        await update(resourceRef, { visibility: 'private', moderated: 'rejected' });
    };

    if (loading) return <Typography>Loading resources for moderation...</Typography>;
    if (error) return <Typography color="error">Error: {error}</Typography>;

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>Resource Moderation</Typography>
            {resources.length > 0 ? (
                resources.map((resource) => (
                    <Card key={resource.id} sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="h6">{resource.title}</Typography>
                            <Typography variant="body2" color="text.secondary"><a href={resource.url} target="_blank" rel="noopener noreferrer">{resource.url}</a></Typography>
                            <Typography variant="caption">Submitted by: {resource.uid}</Typography>
                            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                                <Button variant="contained" color="success" onClick={() => handleApprove(resource.id)}>Approve</Button>
                                <Button variant="contained" color="error" onClick={() => handleReject(resource.id)}>Reject</Button>
                            </Stack>
                        </CardContent>
                    </Card>
                ))
            ) : <Typography>No resources are currently awaiting moderation.</Typography>}
        </Box>
    );
}

export default ResourceModeration;